import React, { useState, useEffect, useCallback } from 'react';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import { toPng } from 'html-to-image';
import autoTable from 'jspdf-autotable';

import jsPDF from 'jspdf';
import '../InvoicePage.css';
import { SERVER_URL, CUTOFF_DATE } from '../../../constants.js'
import { formatLocalDateOnly } from '../../shared utils/dateFormatters.js';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';


function StudentInvoice(props) {
  const { selectedPerson, selectedStay } = props;
  const [student, setStudent] = useState([]);
  const [gross, setGross] = useState(true);
  const [headerNote, setHeaderNote] = useState('');


  const [invoiceDate, setInvoiceDate] = useState(dayjs());

 const DISCOUNT_PREFIX = "DISCOUNT:";

  const isDiscountPayment = (p) =>
  (p?.paymentaccount || "").toUpperCase().startsWith(DISCOUNT_PREFIX);

 
  const handleInvoiceDateChange = (event) => {
    setInvoiceDate(event.target.value);
  };

  const fetchStudent = useCallback(() => {
    const token = sessionStorage.getItem('bearer');
    fetch(`${SERVER_URL}api/students/${selectedPerson.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => {
        if (response.status === 204) {
          return [];
        } else if (response.ok) {
          return response.json();
        } else {
          throw new Error('Failed to fetch student');
        }
      })
      .then((data) => {
        sessionStorage.setItem('bookings', JSON.stringify(data));
        setStudent(data);
      })
      .catch((err) => console.error(err));
  }, [selectedPerson.id]);

  useEffect(() => {
    fetchStudent();
  }, [fetchStudent]);


 
  const [agents, setAgents] = useState([]);
  const fetchAgents = useCallback(() => {
    const token = sessionStorage.getItem('bearer');
    fetch(`${SERVER_URL}api/students/${selectedPerson.id}/agents`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => response.json())
      .then((data) => {
        sessionStorage.setItem('agents', JSON.stringify(data));
        setAgents(data);
      })
      .catch((err) => console.error(err));
  }, [selectedPerson.id]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const [bookings, setBookings] = useState([]);
  const fetchBookings = useCallback(() => {
    const token = sessionStorage.getItem('bearer');
    // Scope bookings to the selected stay (server resolves booking↔stay) so a
    // multi-stay student's invoice no longer lumps every stay together. Fall
    // back to all the student's bookings when no stay is selected.
    const url = selectedStay?.stayId
      ? `${SERVER_URL}api/student/${selectedPerson.id}/stay/${selectedStay.stayId}/bookings`
      : `${SERVER_URL}api/student/${selectedPerson.id}/booking`;
    fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => {
        if (response.status === 204) {
          return [];
        } else if (response.ok) {
          return response.json();
        } else {
          throw new Error('Failed to fetch bookings');
        }
      })
      .then((data) => {
        const filteredData = data.filter(booking =>
          new Date(booking.startDate) > new Date(CUTOFF_DATE)
        );
        sessionStorage.setItem('bookings', JSON.stringify(filteredData));
        setBookings(filteredData);
      })
      .catch((err) => console.error(err));
  }, [selectedPerson.id, selectedStay?.stayId]);
 useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);
  const sortedBookings = [...bookings].sort((a, b) => {
    return new Date(a.startDate) - new Date(b.startDate);
  });

  const [payments, setPayments] = useState([]);
  const fetchPayments = useCallback(() => {
    const token = sessionStorage.getItem('bearer');
    fetch(`${SERVER_URL}api/payments/${selectedPerson.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => {
        if (response.status === 204) { 
          return [];
        } else if (response.ok) {
          return response.json();
        } else {
          throw new Error('Failed to fetch payments');
        }
      })
      .then((data) => {
        // Filter payments that are newer than the cutoff date
      const filteredPayments = data.filter(payment =>
        new Date(payment.paymentdate) >= new Date(CUTOFF_DATE)
      );
      sessionStorage.setItem('payments', JSON.stringify(filteredPayments));
      setPayments(filteredPayments);
      })
      .catch((err) => console.error(err));
  }, [selectedPerson.id]);
  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);
  
  let blissLogoPath;
  blissLogoPath = process.env.PUBLIC_URL + "/assets/img/blissLogo.png";

  const totalCommission = agents && agents.length ? agents.reduce((acc, agent) => {
    return acc + agent.commissionRate;
  }, 0) : 0;
  const factor = (100 - totalCommission) / 100;


  const totalGrossCharge = bookings && bookings.length ? bookings.reduce((acc, booking) => {
    return acc + booking.actualCharge;
  }, 0) : 0;
  
  const totalNetCharge = bookings && bookings.length ? bookings.reduce((acc, booking) => {
    if (booking.commissionable) 
    {
      return acc + booking.actualCharge * factor;
    } else {
      return acc + booking.actualCharge;
    }
  }, 0) : 0;
  
  const discountTotal = payments && payments.length
  ? payments.filter(isDiscountPayment).reduce((acc, p) => acc + (Number(p.paymentamount) || 0), 0)
  : 0;

  const invoiceTotal = gross ? totalGrossCharge : totalNetCharge;
  // deposit should be based on total after discounts (but before cash payments)
const totalAfterDiscounts = Math.max(0, invoiceTotal - discountTotal);

const depositDue = Math.max(200, 0.1 * totalAfterDiscounts);

const paidTotalExcludingDiscounts = payments && payments.length
  ? payments.filter((p) => !isDiscountPayment(p)).reduce((acc, p) => acc + (Number(p.paymentamount) || 0), 0)
  : 0;

// Keep overall balance maths the same (discounts reduce balance like payments)
const totalAlreadyPaid = paidTotalExcludingDiscounts + discountTotal;

const outstandingBalanceGross = totalGrossCharge - totalAlreadyPaid;
const outstandingBalanceNet = totalNetCharge - totalAlreadyPaid;

// Receipt/invoice decision: ONLY real payments count
let rcpinv;
let rcpinvalt;
if (paidTotalExcludingDiscounts === 0) {
  rcpinv = process.env.PUBLIC_URL + "/assets/img/Invoice.png";
  rcpinvalt = "Invoice";
} else {
  rcpinv = process.env.PUBLIC_URL + "/assets/img/receipt.png";
  rcpinvalt = "Receipt";
}


  let footer;
  footer = process.env.PUBLIC_URL + "/assets/img/footer.png";

  const generatePDF = () => {
    const report = new jsPDF('portrait','pt','a4');
     report.html(document.querySelector('#report')).then(() => {
        report.save('report.pdf');
    });
  }

  const drawBrandAccentLabel = (pdf, label, value, x, y, brandPink, brandNavy) => {
  pdf.setFont('helvetica', 'bold');

  const first = label.slice(0, 2);
  const rest = label.slice(2);

  pdf.setTextColor(...brandPink);
  pdf.text(first, x, y);

  const offset1 = pdf.getTextWidth(first);

  pdf.setTextColor(...brandNavy);
  pdf.text(rest + ': ', x + offset1, y);

  const offset2 = pdf.getTextWidth(rest + ': ');

  pdf.setTextColor(40);
  pdf.text(String(value || ''), x + offset1 + offset2, y);
};

const drawBrandAccentText = (pdf, text, xCenter, y, fontSize, brandPink, brandNavy) => {
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(fontSize);

  const words = text.split(' ');
  const parts = [];

  words.forEach((word, wordIndex) => {
    if (!word) return;

    if (word.length >= 2) {
      parts.push({ text: word.slice(0, 2), color: brandPink });
      if (word.length > 2) {
        parts.push({ text: word.slice(2), color: brandNavy });
      }
    } else {
      parts.push({ text: word, color: brandPink });
    }

    if (wordIndex < words.length - 1) {
      parts.push({ text: ' ', color: brandNavy });
    }
  });

  const totalWidth = parts.reduce((sum, part) => sum + pdf.getTextWidth(part.text), 0);
  let x = xCenter - totalWidth / 2;

  parts.forEach((part) => {
    pdf.setTextColor(...part.color);
    pdf.text(part.text, x, y);
    x += pdf.getTextWidth(part.text);
  });
};

  const generatePDF2 = (invoiceDate, studentId, headerNote) => {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: 'a4',
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  let y = 40;

  const brandNavy = [26, 31, 113];
  const brandPink = [230, 0, 126];
  const lightGrey = [245, 245, 245];

  const logo = process.env.PUBLIC_URL + "/assets/img/blissLogo.png";
  const footerImg = process.env.PUBLIC_URL + "/assets/img/footer.png";

  const tableDefaults = {
    theme: 'grid',
    margin: { left: 40, right: 40 },
    styles: {
      fontSize: 10,
      cellPadding: 6,
      textColor: 40,
      lineColor: [180, 180, 180],
      lineWidth: 0.5,
    },
    headStyles: {
      fillColor: brandNavy,
      textColor: 255,
      fontStyle: 'bold',
    },
    alternateRowStyles: {
      fillColor: lightGrey,
    },
  };

  const formattedPdfDate =
    invoiceDate && invoiceDate.format
      ? invoiceDate.format('DD-MM-YYYY')
      : invoiceDate;

  const checkInDate = selectedStay?.arrivalDate
    ? formatLocalDateOnly(new Date(selectedStay.arrivalDate))
    : 'Not available';

  const checkOutDate = selectedStay?.departureDate
    ? formatLocalDateOnly(new Date(selectedStay.departureDate))
    : 'Not available';

  // Header
  const logoWidth = 50;
  const logoHeight = 60;

  pdf.addImage(logo, 'PNG', 40, y, logoWidth, logoHeight);

  drawBrandAccentText(
    pdf,
    paidTotalExcludingDiscounts === 0 ? 'Invoice' : 'Receipt',
    pageWidth - 90,
    y + logoHeight / 2 + 8,
    20,
    brandPink,
    brandNavy
  );

  const lineY = y + logoHeight + 10;
  pdf.setDrawColor(...brandNavy);
  pdf.setLineWidth(1);
  pdf.line(40, lineY, pageWidth - 40, lineY);

  y = lineY + 18;

  pdf.setFontSize(11);
  drawBrandAccentLabel(pdf, 'Student ID', student.mtRef || '', 40, y, brandPink, brandNavy);
  drawBrandAccentLabel(pdf, 'Date', formattedPdfDate, pageWidth - 120, y, brandPink, brandNavy);

  y += 25;

  // Optional free-text header block (e.g. parent name/address, VAT number)
  const noteText = (headerNote || '').trim();
  if (noteText) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(40);
    const noteLines = pdf.splitTextToSize(noteText, pageWidth - 80);
    pdf.text(noteLines, 40, y);
    y += noteLines.length * 13 + 12;
  }

  autoTable(pdf, {
    ...tableDefaults,
    startY: y,
    head: [[
      {
        content: 'Invoice Details',
        colSpan: 4,
        styles: { halign: 'left' }
      }
    ]],
    body: [
      ['First Name', student.studentName || '', 'Surname', student.studentSurname || ''],
      ['Invoice Number', `${student.mtRef || ''}-01`, '', ''],
      ['Check In', checkInDate, 'Check Out', checkOutDate],
    ],
  });

  y = pdf.lastAutoTable.finalY + 18;

  drawBrandAccentText(pdf, 'Booked Items', pageWidth / 2, y, 12, brandPink, brandNavy);
  y += 8;

  autoTable(pdf, {
    ...tableDefaults,
    startY: y,
    head: [['Product', 'Start', 'End', 'Amount']],
    body: sortedBookings.length > 0
      ? sortedBookings.map((booking) => [
          booking.productName || '',
          booking.startDate ? formatLocalDateOnly(new Date(booking.startDate)) : '',
          booking.endDate ? formatLocalDateOnly(new Date(booking.endDate)) : '',
          `£ ${booking.commissionable && !gross
            ? (booking.actualCharge * factor).toFixed(2)
            : booking.actualCharge.toFixed(2)} GBP`
        ])
      : [['No bookings found', '', '', '']],
  });

  y = pdf.lastAutoTable.finalY + 18;

  drawBrandAccentText(pdf, 'Payments and Adjustments', pageWidth / 2, y, 12, brandPink, brandNavy);
  y += 8;

  const paymentRows = payments && payments.length
    ? payments.map((payment) => {
        const discount = isDiscountPayment(payment);
        const formattedPaymentDate = payment.paymentdate
          ? formatLocalDateOnly(new Date(payment.paymentdate))
          : '';

        const label = discount
          ? `Discount applied${formattedPaymentDate ? ` ${formattedPaymentDate}` : ''}`
          : `Payment received${formattedPaymentDate ? ` ${formattedPaymentDate}` : ''}`;

        return [
          label,
          `${discount ? '-£' : '£'} ${Number(payment.paymentamount).toFixed(2)} GBP`
        ];
      })
    : [['No payments recorded', '']];

  autoTable(pdf, {
    ...tableDefaults,
    startY: y,
    head: [['Description', 'Amount']],
    body: paymentRows,
    columnStyles: {
      0: { cellWidth: 340 },
      1: { cellWidth: 'auto', halign: 'right' },
    },
  });

  y = pdf.lastAutoTable.finalY + 18;

  drawBrandAccentText(pdf, 'Totals', pageWidth / 2, y, 12, brandPink, brandNavy);
  y += 8;

  const totalsRows = [
    ['Total', `£ ${(gross ? totalGrossCharge : totalNetCharge).toFixed(2)} GBP`],
  ];

  if (paidTotalExcludingDiscounts === 0) {
    totalsRows.push([
      'Deposit due on receipt of invoice',
      `£ ${depositDue.toFixed(2)} GBP`
    ]);
  }

  totalsRows.push([
    'Balance Due by 15th May',
    `£ ${(gross ? outstandingBalanceGross : outstandingBalanceNet).toFixed(2)} GBP`
  ]);

  autoTable(pdf, {
    ...tableDefaults,
    startY: y,
    head: [['Summary', 'Amount']],
    body: totalsRows,
    columnStyles: {
      0: { cellWidth: 340, fontStyle: 'bold' },
      1: { cellWidth: 'auto', halign: 'right' },
    },
  });

  const finalPage = pdf.getNumberOfPages();
  pdf.setPage(finalPage);

  pdf.setDrawColor(...brandNavy);
  pdf.setLineWidth(1);
  pdf.line(40, pageHeight - 70, pageWidth - 40, pageHeight - 70);

  pdf.addImage(footerImg, 'PNG', (pageWidth - 120) / 2, pageHeight - 55, 120, 35);

  const fullName = `${student.studentName || ''} ${student.studentSurname || ''}`.trim() || String(studentId);
  pdf.save(`${fullName}_${formattedPdfDate}.pdf`);
};
    
  return (
<Container
  fixed
  style={{
    backgroundColor: 'white',
    maxWidth: '17cm',
    fontFamily: 'Roboto, sans-serif',
    fontSize: '14px',
  }}
>
  <React.Fragment>
    <div id="report">
     <div className="images-container">
        <div className="logo-container" style={{ display: 'flex', justifyContent: 'space-between' }}>
          <img alt="Bliss" src={blissLogoPath} style={{ height: '80px' }} />
          <img alt={rcpinvalt} src={rcpinv} style={{ height: '35px', marginTop: '35px' }} />
        </div>
        <hr className="line" style={{ width: '100%' }} />
      </div>
      <div className="student-details-container">
        <div><strong>{student.studentName} {student.studentSurname}</strong></div>
        <div>Student ID: {student.mtRef}</div>
        <div>
          Check in: {selectedStay?.arrivalDate ? formatLocalDateOnly(new Date(selectedStay.arrivalDate)) : 'Not available'}
        </div>
        <div>
          Check out: {selectedStay?.departureDate ? formatLocalDateOnly(new Date(selectedStay.departureDate)) : 'Not available'}
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <p>Date: {invoiceDate ? invoiceDate.format('DD/MM/YYYY') : ''}</p>
        <p>Invoice number: {student.mtRef}-01</p>
      </div>
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <div style={{ textAlign: "center" }}>
          <h2>Booked Items</h2>
        </div>
      </div>
      <div className="bookings-container">
        <table className="bookings-table">
          <tbody>
            {sortedBookings.length > 0 ? (
              sortedBookings.map((booking) => (
                <tr key={booking.bookingId}>
                  <td>{booking.productName}</td>
                  <td>{formatLocalDateOnly(new Date(booking.startDate))}</td>
                  <td>{formatLocalDateOnly(new Date(booking.endDate))}</td>
                  <td>£ {booking.commissionable && !gross ? (booking.actualCharge * factor).toFixed(2) : booking.actualCharge.toFixed(2)} GBP</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4">No bookings found</td>
              </tr>
            )}
            <tr>
              <td colSpan="3" style={{ textAlign: 'right' }}><strong>Total:</strong></td>
              <td>£ {(gross ? totalGrossCharge : totalNetCharge).toFixed(2)} GBP</td>
            </tr>
            {payments && payments.length ? (
  payments.map((payment) => {
    const discount = isDiscountPayment(payment);
    const formattedPaymentDate = payment.paymentdate
      ? formatLocalDateOnly(new Date(payment.paymentdate))
      : "";

    const label = discount
      ? `Discount applied${formattedPaymentDate ? ` ${formattedPaymentDate}` : ""}`
      : `Payment received${formattedPaymentDate ? ` ${formattedPaymentDate}` : ""}`;

    return (
      <tr key={payment.id}>
        <td colSpan="3" style={{ textAlign: "right" }}>{label}</td>
        <td>
          {discount ? "-£" : "£"} {Number(payment.paymentamount).toFixed(2)} GBP
        </td>
      </tr>
    );
  })
) : (
  <tr />
)}


            {paidTotalExcludingDiscounts === 0 && (
  <tr>
    <td colSpan="3" style={{ textAlign: "right" }}>
      <strong>Deposit due on receipt of invoice:</strong>
    </td>
    <td>
      £ {depositDue.toFixed(2)} GBP
    </td>
  </tr>
)}


            <tr>
              <td colSpan="3" style={{ textAlign: 'right' }}><strong>Balance Due by 15th May:</strong></td>
              <td>£ {(gross ? outstandingBalanceGross : outstandingBalanceNet).toFixed(2)} GBP</td>

            </tr>
          </tbody>
        </table>
      </div>
      <hr className="line" />
      <div className="footer-container">
        <img alt="info@brownleeschools.com" src={footer} style={{ height: '80px', width: '200px', marginTop: '35px'}}/>
      </div>
    </div>
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
  <DatePicker
    label="Invoice Date"
    value={invoiceDate}
    onChange={(newValue) => setInvoiceDate(newValue)}
    format="DD/MM/YYYY"
    slotProps={{ textField: { size: 'small' } }}
  />
</LocalizationProvider>
    <TextField
      label="Invoice header note (optional)"
      placeholder={"Parent's full name and address, VAT number, etc."}
      helperText="Free text shown in the invoice header — e.g. parent name/address or VAT details"
      value={headerNote}
      onChange={(e) => setHeaderNote(e.target.value)}
      multiline
      minRows={2}
      fullWidth
      size="small"
      sx={{ mt: 1, mb: 1 }}
    />
    <button onClick={() => setGross(!gross)} type="button">Gross/Net</button>
    <button onClick={() => generatePDF2(invoiceDate, student.id, headerNote)} type="button">Save PDF</button>
    <div>
        <p style={{ color: '#999999', fontSize: '10px' }}>Student: {selectedPerson.id}</p>
        <p style={{ color: '#999999', fontSize: '10px' }}>Is authenticated: {sessionStorage.getItem('isAuthenticated').toString()}</p>
      </div>
  </React.Fragment>
</Container>
  );
}

export default StudentInvoice;
